import { useTranslation } from "react-i18next";
import Card from "../../../../../components/ui/Card";
import { Branch } from "../../../../../utils/supabaseClient";
import { RestaurantTheme } from "../../../../../types/restaurant";
import Toggle from "../../../../../components/ui/Toggle";
import { useEffect } from "react";
import { ThemeType } from "../../../../../types/custom";

type TypographySettingsProps = {
  restaurant: Branch | null;
  updateTheme: (updates: Partial<RestaurantTheme>) => void;
};

const TypographySettings: React.FC<TypographySettingsProps> = ({
  restaurant,
  updateTheme,
}) => {
  const { t } = useTranslation();


  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t("settings.fontFamily")}
        </h3>
        <select
          value={(restaurant?.theme as ThemeType)?.fontFamily}
          onChange={(e) => {
            const value = e.target.value;
            if (value && value.trim()) {
              console.log("Updating titleFontFamily:", value);
              updateTheme({ fontFamily: value });
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
        >
          <option value="Inter" style={{ fontFamily: 'Inter' }}>Inter</option>
          <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
          <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
          <option value="Poppins" style={{ fontFamily: 'Poppins' }}>Poppins</option>
          <option value="Lato" style={{ fontFamily: 'Lato' }}>Lato</option>
          <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
          <option value="Nunito" style={{ fontFamily: 'Nunito' }}>Nunito</option>
          <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
          <option value="Ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
          <option value="Manrope" style={{ fontFamily: 'Manrope' }}>Manrope</option>
          <option value="Work Sans" style={{ fontFamily: 'Work Sans' }}>Work Sans</option>
          <option value="Noto Sans" style={{ fontFamily: 'Noto Sans' }}>Noto Sans</option>
          <option value="Fira Sans" style={{ fontFamily: 'Fira Sans' }}>Fira Sans</option>
          <option value="DM Sans" style={{ fontFamily: 'DM Sans' }}>DM Sans</option>
          <option value="Arial" style={{ fontFamily: 'Arial' }}>Arial</option>
          <option value="Helvetica" style={{ fontFamily: 'Helvetica' }}>Helvetica</option>
          <option value="Segoe UI" style={{ fontFamily: 'Segoe UI' }}>Segoe UI</option>
          <option value="Baskerville Light-Italic" style={{ fontFamily: 'Baskerville Light-Italic' }}>
            Baskerville Light-Italic
          </option>
        </select>
      </Card>
      <Card>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t("settings.titleFontFamily")}
        </h3>
        <select
          value={(restaurant?.theme as ThemeType)?.titleFontFamily}
          onChange={(e) => {
            const value = e.target.value;
            if (value && value.trim()) {
              updateTheme({ titleFontFamily: value });
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
        >
          <option value="Dancing Script" style={{ fontFamily: 'Dancing Script' }}>Dancing Script</option>
          <option value="Pacifico" style={{ fontFamily: 'Pacifico' }}>Pacifico</option>
          <option value="Great Vibes" style={{ fontFamily: 'Great Vibes' }}>Great Vibes</option>
          <option value="Lobster" style={{ fontFamily: 'Lobster' }}>Lobster</option>
          <option value="Satisfy" style={{ fontFamily: 'Satisfy' }}>Satisfy</option>
          <option value="Kaushan Script" style={{ fontFamily: 'Kaushan Script' }}>Kaushan Script</option>
          <option value="Allura" style={{ fontFamily: 'Allura' }}>Allura</option>
          <option value="Alex Brush" style={{ fontFamily: 'Alex Brush' }}>Alex Brush</option>
          <option value="Amatic SC" style={{ fontFamily: 'Amatic SC' }}>Amatic SC</option>
          <option value="Courgette" style={{ fontFamily: 'Courgette' }}>Courgette</option>
          <option value="Fredoka One" style={{ fontFamily: 'Fredoka One' }}>Fredoka One</option>
          <option value="Righteous" style={{ fontFamily: 'Righteous' }}>Righteous</option>
          <option value="Caveat" style={{ fontFamily: 'Caveat' }}>Caveat</option>
          <option value="Indie Flower" style={{ fontFamily: 'Indie Flower' }}>Indie Flower</option>
          <option value="Leckerli One" style={{ fontFamily: 'Leckerli One' }}>Leckerli One</option>
          <option value="Permanent Marker" style={{ fontFamily: 'Permanent Marker' }}>Permanent Marker</option>
          <option value="Cookie" style={{ fontFamily: 'Cookie' }}>Cookie</option>
          <option value="Sacramento" style={{ fontFamily: 'Sacramento' }}>Sacramento</option>
          <option value="Shadows Into Light" style={{ fontFamily: 'Shadows Into Light' }}>Shadows Into Light</option>
          <option value="Berkshire Swash" style={{ fontFamily: 'Berkshire Swash' }}>Berkshire Swash</option>
          <option value="Baskerville Light-Italic" style={{ fontFamily: 'Baskerville Light-Italic' }}>
            Baskerville Light-Italic
          </option>
        </select>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t("settings.fontSizes")}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("settings.titleFontSize")}
            </label>
            <select
              value={(restaurant?.theme as ThemeType)?.titleFontSize || "text-xl"}
              onChange={(e) => {
                const value = e.target.value;
                if (value && value.trim()) {
                  updateTheme({ titleFontSize: value });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
            >
              <option value="text-lg">Large</option>
              <option value="text-xl">Extra Large</option>
              <option value="text-2xl">2X Large</option>
              <option value="text-3xl">3X Large</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("settings.itemFontSize")}
            </label>
            <select
              value={(restaurant?.theme as ThemeType)?.itemFontSize || "text-base"}
              onChange={(e) => {
                const value = e.target.value;
                if (value && value.trim()) {
                  updateTheme({ itemFontSize: value });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
            >
              <option value="text-sm">Small</option>
              <option value="text-md">Medium</option>
              <option value="text-lg">Large</option>
              <option value="text-xl">Extra Large</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("settings.boldTitles")}
            </label>
            <Toggle
              enabled={(restaurant?.theme as ThemeType)?.boldTitles || false}
              onChange={(enabled) => updateTheme({ boldTitles: enabled })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TypographySettings;
